import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { IoWarningOutline } from "react-icons/io5";
import { load } from "@cashfreepayments/cashfree-js";

const quickAmounts = [10, 20, 30, 50, 100, 250, 350, 500, 1000, 2000];

let cashfree;
var initializeSDK = async function () {
    cashfree = await load({
        mode: "production",
    });
};
initializeSDK();


function AddMoney() {
    const navigate = useNavigate();
    const [amount, setAmount] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [userLocation, setUserLocation] = useState(null);
    const [showPaymentSummarySheet, setShowPaymentSummarySheet] = useState(false);


    const handleChange = (e) => {
        const val = e.target.value.replace(/[^0-9]/g, "");
        setAmount(val);
        if (error) setError("");
    };

    const handlePayment = async () => {
        if (!amount || amount < 10) {
            setError("Minimum amount is ₹10");
            return;
        }
        if (amount > 10000) {
            setError("Maximum amount is ₹10,000");
            return;
        }

        setLoading(true);
        setShowPaymentSummarySheet(false);

        try {
            const result = await apiCall(
                "payment",
                {
                    amount: String(amount),
                    platform: 1,
                    longitude: userLocation?.longitude || null,
                    latitude: userLocation?.latitude || null,
                    address: userLocation?.address || null
                },
                true
            );

            if (result.status === true) {
                if (result.order_info.payment_method === "intent") {
                    navigate('/payment/upi-intent', {
                        state: {
                            url: result.order_info.payment_url,
                            payment_order_id: result.order_info.order_id
                        }
                    });
                } else if (result.order_info.payment_method === "web") {
                    toast.error("Web method not installed");
                } else if (result.order_info.payment_method === "razorpay-sdk") {
                    toast.error("Razorpay SDK not installed");
                } else if (result.order_info.payment_method === "cashfree-web-sdk") {
                    let checkoutOptions = {
                        paymentSessionId: result.order_info.payment_session_id,
                        redirectTarget: "_modal",
                    };
                    cashfree.checkout(checkoutOptions).then((result) => {
                        if (result.error) {
                            // This will be true whenever the user clicks on the close icon inside the modal or any error happens during the payment
                            console.log("User has closed the popup or there is some payment error, Check for Payment Status");
                            console.log(result.error);
                        }
                        if (result.redirect) {
                            // This will be true when the payment redirection page can't be opened in the same window
                            // This is an exceptional case only when the page is opened inside an inAppBrowser
                            // In this case the customer will be redirected to the return URL once payment is completed
                            console.log("Payment will be redirected");
                        }
                        if (result.paymentDetails) {
                            // This will be called whenever the payment is completed irrespective of transaction status
                            console.log("Payment has been completed, Check for Payment Status");
                            console.log(result.paymentDetails.paymentMessage);
                        }
                    });
                } else if (result.order_info.payment_method === "cashfree-sdk") {
                    if (window.ReactNativeWebView) {
                        window.ReactNativeWebView.postMessage(
                            JSON.stringify({
                                type: "OPEN_CASHFREE_SDK",
                                data: {
                                    payment_session_id: result.order_info.payment_session_id,
                                    order_id: result.order_info.order_id
                                }
                            })
                        );
                    } else {
                        toast.error("Razorpay SDK not installed");
                        //window.open(`https://rechargecircle.in/payment-cashfree.html?session_id=${result.order_info.payment_session_id}`, "_blank");
                    }
                }
            } else {
                toast.error(result.message || "Failed to fetch bill");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    const handleLocation = async () => {
        setLocationLoading(true);

        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: "GET_LOCATION_INFO"
                })
            );
        } else {
            setShowPaymentSummarySheet(true);
            toast.error("Location service not available");
            setLocationLoading(false);
        }
    };

    window.addEventListener("message", async (event) => {
        try {
            // Check if event.data is a string or object
            let data;
            if (typeof event.data === 'string') {
                data = JSON.parse(event.data);
            } else {
                // If it's already an object, use it directly
                data = event.data;
            }

            if (data.type === "LOCATION_INFO") {
                setUserLocation(data);
                setLocationLoading(false);
                setShowPaymentSummarySheet(true);
            }
        } catch (e) {
            setLocationLoading(false);
            toast.error("Error in message listener:", e.message);
        }
    });

    const selectedAmount = (amt) => {
        setAmount(String(amt));
        setError("");
        handleLocation();
    };

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <Header title={"Add Money"} showBack={true} />

            <div className="flex-1 flex flex-col p-4">
                {/* Suggestions at Top */}
                <div className="mb-6">
                    <div className="text-sm font-semibold mb-3 pl-1" style={{ color: APP_THEME.textSecondary }}>
                        Suggested amount
                    </div>
                    <div className="flex gap-2.5 flex-wrap">
                        {quickAmounts.map((amt) => (
                            <button
                                key={amt}
                                onClick={() => selectedAmount(amt)}
                                className="py-2.5 px-5 rounded-xl text-sm font-bold cursor-pointer transition-all duration-200 active:scale-95"
                                style={{
                                    border: `1.5px solid ${Number(amount) === amt ? APP_THEME.primaryColor : APP_THEME.borderColor}`,
                                    background: Number(amount) === amt ? APP_THEME.primaryLight : APP_THEME.surfaceColor,
                                    color: Number(amount) === amt ? APP_THEME.primaryColor : APP_THEME.textPrimary,
                                }}
                            >
                                ₹{amt}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Amount Input */}
                <div className="mb-5">
                    <div
                        className="flex items-center overflow-hidden transition-colors duration-200 p-1 rounded-xl"
                        style={{
                            border: `1.5px solid ${error ? APP_THEME.errorColor : APP_THEME.borderColor}`,
                            background: APP_THEME.surfaceColor,
                        }}
                    >
                        <div className="px-3 py-2 text-lg font-bold" style={{ color: APP_THEME.primaryColor }}>₹</div>
                        <input
                            type="tel"
                            placeholder="Enter custom amount"
                            value={amount}
                            onChange={handleChange}
                            className="flex-1 py-2 border-none text-lg font-semibold outline-none bg-transparent"
                            style={{ color: APP_THEME.textPrimary }}
                            onFocus={(e) => (e.target.parentElement.style.borderColor = APP_THEME.primaryColor)}
                            onBlur={(e) => (e.target.parentElement.style.borderColor = error ? APP_THEME.errorColor : APP_THEME.borderColor)}
                        />
                    </div>
                    {error && (
                        <div className="text-xs mt-1 pl-1" style={{ color: APP_THEME.errorColor }}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Warning Note with React Icon */}
                <div
                    className="mb-4 p-3 rounded-lg text-xs"
                    style={{
                        border: `1.5px solid ${APP_THEME.errorColor}`,
                        background: `${APP_THEME.errorColor}08`,
                        color: APP_THEME.errorColor,
                    }}
                >
                    <div className="flex items-start gap-2">
                        <IoWarningOutline size={18} className="flex-shrink-0 mt-0.5" />
                        <span>
                            The amount in wallet is only for use in Mobile Recharge, DTH Recharge and Google Play Redeem code and will not be permitted for any bill payment.
                        </span>
                    </div>
                </div>

                {/* Add Money Button */}
                <button
                    onClick={() => handleLocation()}
                    disabled={loading || locationLoading}
                    className="w-full h-12 border-none rounded-lg text-[15px] font-semibold cursor-pointer transition-all duration-200 active:scale-98 mb-4 flex items-center justify-center gap-2"
                    style={{
                        background: (loading || locationLoading) ? APP_THEME.primaryColor + '88' : APP_THEME.primaryColor,
                        color: APP_THEME.textOnPrimary,
                        borderRadius: APP_THEME.borderRadiusBtn,
                        boxShadow: `0 4px 8px ${APP_THEME.primaryColor}22`,
                        opacity: (loading || locationLoading) ? 0.7 : 1,
                        cursor: (loading || locationLoading) ? 'not-allowed' : 'pointer',
                    }}
                >
                    {(loading || locationLoading) ? (
                        <>
                            <svg
                                className="animate-spin h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                />
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                            </svg>
                            {locationLoading ? 'Fetching Location...' : 'Processing...'}
                        </>
                    ) : (
                        'Add Money to Wallet'
                    )}
                </button>
            </div>

            {/* Location Loading Overlay */}
            {locationLoading && (
                <>
                    {/*
                    <div
                        className="fixed inset-0 backdrop-blur-md bg-black/40 z-[1200] animate-fadeIn"
                        style={{ backdropFilter: 'blur(8px)' }}
                    />
                    <div className="fixed inset-0 z-[1201] flex items-center justify-center">
                        <div className="bg-white rounded-2xl p-8 flex flex-col items-center shadow-2xl animate-scaleIn max-w-[320px] w-full mx-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full border-4 border-gray-200 border-t-4 animate-spin"
                                    style={{ borderTopColor: APP_THEME.primaryColor }} />
                            </div>
                            <div className="mt-4 text-center">
                                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>
                                    Fetching Location
                                </h3>
                                <p className="text-sm mt-1" style={{ color: APP_THEME.textSecondary }}>
                                    Please allow location access
                                </p>
                            </div>
                        </div>
                    </div>
                    */}
                </>
            )}

            {/* Payment Summary Bottom Sheet */}
            {showPaymentSummarySheet && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-[1100] animate-fadeIn"
                        onClick={() => setShowPaymentSummarySheet(false)}
                    />
                    <div
                        className="fixed bottom-0 left-0 right-0 z-[1101] rounded-t-2xl animate-slideUp max-h-[85vh] overflow-y-auto"
                        style={{ background: APP_THEME.bgColor }}
                    >
                        {/* Header */}
                        <div className="sticky top-0 pt-3 pb-2 px-5 border-b" style={{ background: APP_THEME.bgColor, borderColor: APP_THEME.borderColor }}>
                            <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: APP_THEME.borderColor }} />
                            <div className="flex justify-between items-center">
                                <h3 className="text-lg font-semibold" style={{ color: APP_THEME.textPrimary }}>Payment Summary</h3>
                                <button onClick={() => setShowPaymentSummarySheet(false)} className="p-1 rounded-full active:scale-90 transition-transform">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18" />
                                        <line x1="6" y1="6" x2="18" y2="18" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-5 pb-6">
                            {/* Amount Display */}
                            <div className="mb-4 p-4 rounded-xl" style={{ background: APP_THEME.surfaceColor }}>
                                <div className="flex justify-between items-center">
                                    <span className="text-base font-semibold" style={{ color: APP_THEME.textPrimary }}>Amount to Add</span>
                                    <span className="text-2xl font-bold" style={{ color: APP_THEME.primaryColor }}>
                                        ₹{amount}
                                    </span>
                                </div>
                            </div>

                            {/* Confirm Button */}
                            <button
                                onClick={handlePayment}
                                disabled={loading}
                                className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
                                style={{
                                    background: loading ? APP_THEME.textSecondary : APP_THEME.primaryColor,
                                    color: APP_THEME.textOnPrimary,
                                    borderRadius: APP_THEME.borderRadiusBtn,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                {loading ? (
                                    <>
                                        <div className="w-5 h-5 rounded-full animate-spin" style={{ border: `2px solid #ffffff33`, borderTopColor: "#fff" }} />
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    `Confirm ₹${amount}`
                                )}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <Toaster position="bottom-center" />

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                    .active\\:scale-95:active {
                        transform: scale(0.95);
                    }
                    .active\\:scale-90:active {
                        transform: scale(0.9);
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; }
                        to { opacity: 1; }
                    }
                    @keyframes slideUp {
                        from { transform: translateY(100%); }
                        to { transform: translateY(0); }
                    }
                    @keyframes scaleIn {
                        from { 
                            opacity: 0;
                            transform: scale(0.9);
                        }
                        to { 
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                    .animate-spin {
                        animation: spin 0.8s linear infinite;
                    }
                    .animate-fadeIn {
                        animation: fadeIn 0.2s ease forwards;
                    }
                    .animate-slideUp {
                        animation: slideUp 0.3s ease forwards;
                    }
                    .animate-scaleIn {
                        animation: scaleIn 0.3s ease forwards;
                    }
                `}
            </style>
        </div>
    );
}

export default AddMoney;